'use client';

import {
	composeRenderProps,
	DialogTrigger as DialogTriggerPrimitive,
	ModalOverlay,
	Modal as ModalPrimitive,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

import type { DialogProps, DialogTriggerProps, ModalOverlayProps } from 'react-aria-components';
import type { VariantProps } from 'tailwind-variants';

import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogCloseIcon,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from './Dialog';

const Modal = (props: DialogTriggerProps) => {
	return <DialogTriggerPrimitive {...props} />;
};

const modalOverlayStyles = tv({
	base: [
		'fixed top-0 left-0 isolate z-50 h-(--visual-viewport-height) w-full p-4',
		'bg-light-background/50 flex items-center justify-center',
		'[--visual-viewport-vertical-padding:16px]',
	],
	variants: {
		isBlurred: {
			true: 'supports-backdrop-filter:bg-light-background/15 supports-backdrop-filter:backdrop-blur',
		},
	},
});

const modalContentStyles = tv({
	base: [
		'bg-neutral-white text-light-text ring-neutral-lighter shadow-level-2 overflow-hidden rounded-[10px] text-left align-middle ring-2',
		'relative mx-auto my-auto max-h-[calc(100vh-2rem)] w-full',
	],
	variants: {
		size: {
			xs: 'md:max-w-xs',
			sm: 'md:max-w-sm',
			md: 'md:max-w-md',
			lg: 'md:max-w-lg',
			xl: 'md:max-w-xl',
			'2xl': 'md:max-w-2xl',
			'3xl': 'md:max-w-3xl',
			'4xl': 'md:max-w-4xl',
			'5xl': 'md:max-w-5xl',
		},
	},
	defaultVariants: {
		size: 'lg',
	},
});

interface ModalContentProps
	extends Omit<ModalOverlayProps, 'className' | 'children'>,
		Pick<DialogProps, 'aria-label' | 'aria-labelledby' | 'role' | 'children'>,
		VariantProps<typeof modalContentStyles> {
	closeButton?: boolean;
	isBlurred?: boolean;
	classNames?: {
		overlay?: ModalOverlayProps['className'];
		content?: ModalOverlayProps['className'];
	};
}

const ModalContent = ({
	classNames,
	isDismissable: isDismissableInternal,
	isBlurred = false,
	children,
	size,
	role = 'dialog',
	closeButton = false,
	...props
}: ModalContentProps) => {
	const isDismissable = isDismissableInternal ?? role !== 'alertdialog';

	return (
		<ModalOverlay
			isDismissable={isDismissable}
			className={composeRenderProps(classNames?.overlay, (className, renderProps) =>
				modalOverlayStyles({
					...renderProps,
					isBlurred,
					className,
				}),
			)}
			{...props}
		>
			<ModalPrimitive
				isDismissable={isDismissable}
				className={composeRenderProps(classNames?.content, (className, renderProps) =>
					modalContentStyles({
						...renderProps,
						size,
						className,
					}),
				)}
				{...props}
			>
				<Dialog role={role}>
					{values => (
						<>
							{typeof children === 'function' ? children(values) : children}
							{closeButton && <DialogCloseIcon isDismissable={isDismissable} />}
						</>
					)}
				</Dialog>
			</ModalPrimitive>
		</ModalOverlay>
	);
};

const ModalTrigger = DialogTrigger;
const ModalHeader = DialogHeader;
const ModalTitle = DialogTitle;
const ModalDescription = DialogDescription;
const ModalFooter = DialogFooter;
const ModalBody = DialogBody;
const ModalClose = DialogClose;

Modal.Trigger = ModalTrigger;
Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Description = ModalDescription;
Modal.Footer = ModalFooter;
Modal.Body = ModalBody;
Modal.Close = ModalClose;
Modal.Content = ModalContent;

export { Modal };
